// Hardcharger™ launch facts: THE ONE FILE to edit on GA day.
//
//   LAUNCH_DATE          the announcement day: blog post and news dates, their JSON-LD, the sitemap's lastmod.
//   HARDCHARGER_VERSION  the latest release when this build was made: the prerendered HTML, llms.txt and the FAQ
//                        text name it. Pages that show a version to copy (the home-page terminal, the install
//                        commands and layout, the /chargewire discovery example) start from it and switch to the
//                        latest GitHub release once loaded (useHardchargerVersions); the desktop downloads follow
//                        the latest release. Bump it (and CHARGEWIRE_VERSION) with each release, so the HTML a
//                        crawler or a visitor without JavaScript gets is current too.
//   TPCH_SF1000          the 1 TB TPC-H-derived run: rows, per-query highlights, and `pendingGaRun`. A GA re-run
//                        replaces the rows (label, engine, seconds, cost), `queries`, `date`, `hardchargerBuild`,
//                        `queryRuns`, `secondPassWithinPercent` (and `engineCommitsNewer` if still relevant) and sets
//                        `pendingGaRun: false`; every page, the blog post, the chart SVG, llms.txt and the
//                        Portuguese site follow from here.
//   CLICKBENCH           the ClickBench dry run (concurrency talking point), same `pendingGaRun` idea.
//
// This module must stay dependency-free: scripts/check-catalogs.mjs, scripts/generate-seo.mjs and
// scripts/hardcharger-benchmark-svg.mjs evaluate it outside Vite, and the i18n catalogs import it.
// Every number here comes from a measured run cited next to it (global CLAUDE.md: no unverified claims).

export const LAUNCH_DATE = '2026-10-21';
export const HARDCHARGER_VERSION = '2.5.0';
/** The first release with the desktop app (copy says "v2.1.0+"). */
export const HARDCHARGER_DESKTOP_MIN_VERSION = '2.1.0';
export const DUCKDB_VERSION = '2.0.0';
export const CHARGEWIRE_VERSION = '2.0.2';
/** Pages whose content changes with the launch; the sitemap marks them modified on LAUNCH_DATE. */
export const LAUNCH_ROUTES = ['/', '/hardcharger', '/hardcharger/install', '/hardcharger/integrations', '/chargewire', '/news', '/blog', '/blog/introducing-hardcharger'];

export type LaunchLocale = 'en' | 'pt-BR';

// TPC-H SF1000 (1 TB), 2026-10-20: gizmosql-2.0-design launch/benchmarks/2026-10-20-tpch-sf1000-ga/report.md.
// One AWS r9gd.24xlarge (Graviton5, 96 vCPU, 742 GiB, 3 x 1.9 TB NVMe RAID 0), on demand in us-east-2c at
// $7.68864/h; sum of the 22 per-query means (3 runs each); cost = seconds × hourly rate / 3600.
export const TPCH_SF1000 = {
  date: '2026-10-20',
  /** True while the rows are the 2.0.0-rc.1 numbers on a pre-release DuckDB 2.0. Set to false with the GA re-run. */
  pendingGaRun: false,
  /** The Hardcharger build that was measured. */
  hardchargerBuild: '2.0.0',
  machine: 'AWS r9gd.24xlarge (Graviton5, 96 vCPU, 742 GiB, 3 × 1.9 TB NVMe)',
  hourlyUsd: 7.68864,
  /** Our own earlier GizmoSQL run of the same workload (/blog/tpch-1tb-graviton5-80-seconds). */
  publishedDate: '2026-09-04',
  rows: [
    { id: 'published', label: 'GizmoSQL, published result (Sept 4, 2026)', engine: 'DuckDB 1.5.5', seconds: 80.475, costUsd: 0.172 },
    { id: 'stable', label: 'GizmoSQL 1.42.0 (earlier DuckDB 1.5.6 baseline)', engine: 'DuckDB 1.5.6', seconds: 76.957, costUsd: 0.164 },
    { id: 'edge', label: 'GizmoSQL 1.42.0, rebuilt on DuckDB 2.0.0', engine: 'DuckDB 2.0.0', seconds: 42.179246346, costUsd: 0.090083622 },
    { id: 'hardcharger', label: 'Hardcharger 2.0.0', engine: 'DuckDB 2.0.0', seconds: 42.351494869, costUsd: 0.090451499, highlight: true },
  ],
  /** Selected per-query means; the 1.5.6 stable column is the archived earlier baseline. */
  queries: [
    { query: 'Q09', stable: 13.34, edge: 5.136530240, hardcharger: 5.075546900 },
    { query: 'Q21', stable: 10.83, edge: 2.908878962, hardcharger: 2.931931655 },
    { query: 'Q14', stable: 2.54, edge: 1.064720074, hardcharger: 1.053911448 },
    { query: 'Q20', stable: 1.91, edge: 0.861363729, hardcharger: 0.871291002 },
    { query: 'Q22', stable: 1.67, edge: 0.749117374, hardcharger: 0.747628609 },
  ],
  /** Hardcharger's DuckDB build was this many commits newer than the edge channel's (rc.1 run only). */
  engineCommitsNewer: 0,
  /** 22 queries × 3 runs × 2 servers × 2 passes, with matching row counts. The older 1.5.x baselines are historical. */
  queryRuns: 264,
  secondPassWithinPercent: 2,
};

// Visitor-facing charts compare Hardcharger with the historical released-server baselines.
// Keep the complete matched-engine measurements above for analysis and the full methods.
export const TPCH_CHART_ROWS = TPCH_SF1000.rows
  .filter(row => row.id === 'stable' || row.id === 'hardcharger')
  .map(row => row.id === 'stable' ? { ...row, label: 'GizmoSQL 1.42.0' } : row);

// ClickBench dry run, 2026-10-20 (gizmodata/hardcharger bench/clickbench @ 57410c13, PR.md): the same c6a.4xlarge
// machine class and upstream harness as GizmoSQL's published entry (2026-05-11). Not submitted; the GA run on 2.0.0
// is the one that goes into the ClickBench PR.
export const CLICKBENCH = {
  date: '2026-10-20',
  pendingGaRun: true,
  hardchargerBuild: '2.0.0-rc.1',
  machine: 'c6a.4xlarge',
  publishedDate: '2026-05-11',
  gizmosqlPublished: { loadSeconds: 123, coldSumSeconds: 116.29, hotSumSeconds: 21.03, concurrencyQps: 0.117, errorRatio: 0.901 },
  hardcharger: { loadSeconds: 109, coldSumSeconds: 115.09, hotSumSeconds: 20.08, concurrencyQps: 0.350, errorRatio: 0 },
};

type Row = (typeof TPCH_SF1000.rows)[number];
export const tpchRow = (id: Row['id']): Row => {
  const row = TPCH_SF1000.rows.find(r => r.id === id);
  if (!row) throw new Error(`TPCH_SF1000 has no row ${id}`);
  return row;
};

/** Engine labels as the Portuguese site prints them; anything unlisted stays as written. */
const ENGINE_LABELS_PT: Record<string, string> = { 'DuckDB 2.0 pre-release': 'DuckDB 2.0 em pré-lançamento' };
export const engineLabel = (locale: LaunchLocale, engine: string) => (locale === 'pt-BR' ? ENGINE_LABELS_PT[engine] ?? engine : engine);

const formatters = (locale: LaunchLocale) => {
  const number = (value: number, digits: number) => new Intl.NumberFormat(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(value);
  // A plain space after "US$", as the hand-written Portuguese prose has (Intl uses a no-break space).
  const usd = (value: number, digits: number) => new Intl.NumberFormat(locale, { style: 'currency', currency: 'USD', minimumFractionDigits: digits, maximumFractionDigits: digits }).format(value).replace(/\u00a0/g, ' ');
  const date = (value: string) => new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' }).format(new Date(`${value}T00:00:00Z`));
  // ISO on the English site, dd/mm/yyyy on the Portuguese one (llms.txt).
  const dateShort = (value: string) => (locale === 'pt-BR' ? value.split('-').reverse().join('/') : value);
  return { number, usd, date, dateShort };
};

/** The TPC-H-derived numbers as the prose prints them, per site language. Everything a sentence needs, pre-formatted. */
export function tpchCopy(locale: LaunchLocale) {
  const { number, usd, date, dateShort } = formatters(locale);
  const pt = locale === 'pt-BR';
  const hc = tpchRow('hardcharger'), published = tpchRow('published'), stable = tpchRow('stable'), edge = tpchRow('edge');
  const build = TPCH_SF1000.hardchargerBuild;
  const pending = TPCH_SF1000.pendingGaRun;
  const hcLabel = `Hardcharger ${build}`;
  return {
    pending,
    build,
    hcLabel,
    date: date(TPCH_SF1000.date),
    dateIso: TPCH_SF1000.date,
    dateShort: dateShort(TPCH_SF1000.date),
    hourly: usd(TPCH_SF1000.hourlyUsd, 2),
    secondPassWithinPercent: String(TPCH_SF1000.secondPassWithinPercent),
    hcSeconds: number(hc.seconds, 1),
    hcSecondsExact: number(hc.seconds, 3),
    hcSecondsUnder: String(Math.ceil(hc.seconds)),
    hcCost: usd(hc.costUsd, 3),
    hcCostRounded: usd(hc.costUsd, 2),
    hcCents: String(Math.round(hc.costUsd * 100)),
    hcEngine: engineLabel(locale, hc.engine),
    edgeEngine: engineLabel(locale, edge.engine),
    stableEngine: engineLabel(locale, stable.engine),
    publishedEngine: engineLabel(locale, published.engine),
    publishedSeconds: number(published.seconds, 1),
    publishedSecondsExact: number(published.seconds, 3),
    publishedCost: usd(published.costUsd, 3),
    stableSeconds: number(stable.seconds, 1),
    stableSecondsExact: number(stable.seconds, 3),
    stableCost: usd(stable.costUsd, 3),
    edgeSeconds: number(edge.seconds, 1),
    edgeSecondsExact: number(edge.seconds, 3),
    edgeCost: usd(edge.costUsd, 3),
    speedupVsPublished: number(published.seconds / hc.seconds, 1),
    speedupVsStable: number(stable.seconds / hc.seconds, 1),
    lessTimeThanEdgePercent: String(Math.round((1 - hc.seconds / edge.seconds) * 100)),
    commitsNewer: number(TPCH_SF1000.engineCommitsNewer, 0),
    queryRuns: number(TPCH_SF1000.queryRuns, 0),
    queries: TPCH_SF1000.queries.map(q => ({ query: q.query, stable: number(q.stable, 2), edge: number(q.edge, 2), hardcharger: number(q.hardcharger, 2) })),
    /** "(release candidate)" while the numbers are the rc.1 run's. */
    qualifier: pending ? (pt ? ' (versão candidata)' : ' (release candidate)') : '',
    /** The hero footnote: which build produced the headline numbers. */
    buildNote: pending
      ? (pt ? `Hardcharger ${build} sobre uma versão de pré-lançamento do DuckDB 2.0` : `Hardcharger ${build} on a pre-release DuckDB 2.0 build`)
      : (pt ? `Hardcharger ${build} com DuckDB ${DUCKDB_VERSION}` : `Hardcharger ${build} on DuckDB ${DUCKDB_VERSION}`),
    /** One sentence on the numbers' status, for FAQ answers and news copy. Empty once the GA run is in. */
    statusSentence: pending
      ? (pt ? ' São números de versão candidata, sobre uma versão de pré-lançamento do DuckDB 2.0; vamos refazê-los no DuckDB 2.0.0.' : ' These are release-candidate numbers on a pre-release DuckDB 2.0 build; we will re-run them on DuckDB 2.0.0.')
      : '',
    /** Context for the public two-row comparison: earlier engine baseline versus Hardcharger. */
    historicalEngineCaveat: pt
      ? `O Hardcharger usa DuckDB ${DUCKDB_VERSION}; a referência histórica do GizmoSQL usa DuckDB 1.5.6. A diferença de desempenho inclui as mudanças no mecanismo e não isola o efeito do protocolo do servidor.`
      : `Hardcharger uses DuckDB ${DUCKDB_VERSION}; the historical GizmoSQL baseline uses DuckDB 1.5.6. The performance difference includes changes in the engine and does not isolate the effect of the server protocol.`,
    /** The engine caveat printed with the chart and in the blog post. */
    engineCaveat: pending
      ? (pt
        ? `Números de versão candidata: o Hardcharger ${build} e o canal edge do GizmoSQL rodaram versões de pré-lançamento do DuckDB 2.0, e a do Hardcharger é mais nova (${number(TPCH_SF1000.engineCommitsNewer, 0)} commits). Esta execução não separa o servidor do mecanismo. Vamos refazer a execução de todos os servidores no DuckDB 2.0.0 GA e atualizar este gráfico.`
        : `Release-candidate numbers: Hardcharger ${build} and GizmoSQL’s edge channel both ran pre-release DuckDB 2.0 builds, and Hardcharger’s is newer (${number(TPCH_SF1000.engineCommitsNewer, 0)} commits). This run does not separate the server from the engine. We will re-run every server on DuckDB 2.0.0 GA and update this chart.`)
      : (pt
        ? `O Hardcharger ${build} e o GizmoSQL 1.42.0 recompilado com ajustes de compatibilidade rodaram o mesmo commit DuckDB ${DUCKDB_VERSION}. Os totais estão praticamente empatados; as linhas DuckDB 1.5.x são referências históricas.`
        : `Hardcharger ${build} and GizmoSQL 1.42.0 rebuilt with compatibility patches both ran the same DuckDB ${DUCKDB_VERSION} commit. Their totals are essentially tied; the older DuckDB 1.5.x rows are historical baselines.`),
    /** The blog post's version of the engine caveat (a list item), or nothing once the GA run is in. */
    engineCaveatHtml: pending
      ? (pt
        ? `<li><strong>As builds do mecanismo são diferentes.</strong> A versão de pré-lançamento do DuckDB 2.0 do Hardcharger ${build} tem ${number(TPCH_SF1000.engineCommitsNewer, 0)} commits a mais que a do canal edge do GizmoSQL, então esta execução não separa o servidor do mecanismo. Vamos executar novamente todos os servidores na mesma versão 2.0.0 do DuckDB e atualizar este artigo.</li>`
        : `<li><strong>The engine builds differ.</strong> Hardcharger ${build}'s DuckDB 2.0 pre-release is ${number(TPCH_SF1000.engineCommitsNewer, 0)} commits newer than the one in GizmoSQL's edge channel, so this run doesn't separate the server from the engine. We will re-run every server on the same DuckDB 2.0.0 release and update this post.</li>`)
      : '',
    /** How the blog post and the chart describe Hardcharger's engine for this run. */
    hcEngineDetail: pending
      ? (pt ? 'uma versão de pré-lançamento mais recente do DuckDB 2.0' : 'a newer DuckDB 2.0 pre-release')
      : (pt ? `DuckDB ${DUCKDB_VERSION}` : `DuckDB ${DUCKDB_VERSION}`),
    /** llms.txt's one-line status of the numbers. */
    llmsStatus: pending
      ? (pt ? 'Números de versão candidata, sobre uma versão de pré-lançamento do DuckDB 2.0 (mais nova que a usada pelo canal edge do GizmoSQL com que foi comparado), cliente na mesma máquina, apenas configurações padrão; serão refeitos no DuckDB 2.0.0 GA.' : 'Release-candidate numbers on a pre-release DuckDB 2.0 build (newer than the DuckDB build in the GizmoSQL edge channel it was compared with), client on the same machine, defaults only; to be re-run on DuckDB 2.0.0 GA.')
      : (pt ? `Hardcharger ${build} com DuckDB ${DUCKDB_VERSION}; cliente na mesma máquina, apenas configurações padrão.` : `Hardcharger ${build} on DuckDB ${DUCKDB_VERSION}; client on the same machine, defaults only.`),
    llmsStatusShort: pending
      ? (pt ? `números de versão candidata em versões de pré-lançamento do DuckDB 2.0 (a build do DuckDB usada pelo Hardcharger é mais recente), que serão refeitos no DuckDB 2.0.0` : `release-candidate numbers on DuckDB 2.0 pre-releases (Hardcharger's DuckDB build is newer), to be re-run on DuckDB 2.0.0`)
      : (pt ? `Hardcharger ${build} com DuckDB ${DUCKDB_VERSION}` : `Hardcharger ${build} on DuckDB ${DUCKDB_VERSION}`),
  };
}

/** The caveats printed beside the chart on /hardcharger (launch/website-material.md, "Say this honestly"). */
export function tpchCaveats(locale: LaunchLocale): string[] {
  const c = tpchCopy(locale);
  return locale === 'pt-BR'
    ? [
      c.historicalEngineCaveat,
      'O cliente Hardcharger rodou na mesma máquina, com TLS (127.0.0.1). As respostas têm poucas linhas cada, então a rede acrescenta pouco.',
      'As primeiras execuções a frio estão incluídas nas médias (cache de páginas descartado e servidor reiniciado entre as rodadas). A linha DuckDB 1.5.6 é uma referência histórica de uma execução anterior.',
      'A consulta 11 usa a fração fixa do seu gerador, não uma ajustada ao tamanho dos dados, então não retorna linhas em nenhum servidor.',
      `Apenas configurações padrão: 96 threads, o limite de memória padrão do DuckDB, nada ajustado. Uma segunda rodada completa ficou a menos de ${TPCH_SF1000.secondPassWithinPercent}% da primeira, e todas as consultas Hardcharger retornaram as contagens de linhas esperadas.`,
    ]
    : [
      c.historicalEngineCaveat,
      'The Hardcharger client ran on the same machine over TLS (127.0.0.1). The answers are a few rows each, so the network adds little.',
      'Cold first runs are included in the means (page cache dropped and server restarted between passes). The DuckDB 1.5.6 row is a historical baseline from an earlier run.',
      'Query 11 uses its generator’s fixed fraction, not one scaled to the data size, so it returns no rows on every server.',
      `Defaults only: 96 threads, DuckDB’s default memory limit, nothing tuned. A second full pass came within ${TPCH_SF1000.secondPassWithinPercent}% of the first, and every Hardcharger query returned the expected row count.`,
    ];
}

/** The ClickBench concurrency talking point, per site language. */
export function clickbenchCopy(locale: LaunchLocale) {
  const { number, date } = formatters(locale);
  const pt = locale === 'pt-BR';
  const b = CLICKBENCH;
  const qpsMultiple = number(b.hardcharger.concurrencyQps / b.gizmosqlPublished.concurrencyQps, 0);
  const failedPercent = number(b.gizmosqlPublished.errorRatio * 100, 0);
  const status = b.pendingGaRun
    ? (pt ? ` Execução preliminar do Hardcharger ${b.hardchargerBuild} (${date(b.date)}), aguardando a execução na versão GA.` : ` Hardcharger ${b.hardchargerBuild} dry run (${date(b.date)}), pending the GA run.`)
    : (pt ? ` Hardcharger ${b.hardchargerBuild}, ${date(b.date)}.` : ` Hardcharger ${b.hardchargerBuild}, ${date(b.date)}.`);
  return {
    pending: b.pendingGaRun,
    qpsMultiple,
    failedPercent,
    hcQps: number(b.hardcharger.concurrencyQps, 3),
    publishedQps: number(b.gizmosqlPublished.concurrencyQps, 3),
    title: pt ? `Concorrência: ${qpsMultiple}× as consultas por segundo, sem erros` : `Concurrency: ${qpsMultiple}× the QPS with zero errors`,
    /** llms.txt's parenthetical on the ClickBench numbers' status. */
    llmsStatus: b.pendingGaRun
      ? (pt ? ` (Hardcharger ${b.hardchargerBuild}, ${date(b.date)}, aguardando a execução na versão GA)` : ` (Hardcharger ${b.hardchargerBuild}, ${date(b.date)}, pending the GA run)`)
      : ` (Hardcharger ${b.hardchargerBuild}, ${date(b.date)})`,
    /** llms.txt's label for the run: says "dry run" only while the GA run is pending. */
    llmsLabel: b.pendingGaRun ? (pt ? 'execução preliminar no ClickBench' : 'ClickBench dry run') : 'ClickBench',
    sentence: pt
      ? `Na fase de concorrência do ClickBench, o Hardcharger atingiu ${qpsMultiple}× as consultas por segundo da entrada publicada do GizmoSQL no ClickBench (${number(b.hardcharger.concurrencyQps, 3)} contra ${number(b.gizmosqlPublished.concurrencyQps, 3)} QPS) sem nenhum erro, na mesma classe de máquina (${b.machine}); na entrada do GizmoSQL, ${failedPercent}% das consultas concorrentes falharam.${status}`
      : `In ClickBench's concurrency phase, Hardcharger reached ${qpsMultiple}× the queries per second of GizmoSQL's published ClickBench entry (${number(b.hardcharger.concurrencyQps, 3)} vs ${number(b.gizmosqlPublished.concurrencyQps, 3)} QPS) with zero errors, on the same machine class (${b.machine}); in GizmoSQL's entry, ${failedPercent}% of the concurrent queries failed.${status}`,
  };
}
